import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";
import {
  ModalidadRenglon,
  MonitoreoFiltro,
  Ubicacion,
} from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";
import { ModalModalidadComponent } from "./modal-modalidad/modal-modalidad.component";

/**
 * Donde se espera a cada persona: oficina, casa o mixto.
 *
 * ES EL PRIMER PASO DEL MODULO, no un catalogo mas. Quien no tiene
 * modalidad asignada no tiene sitio esperado, asi que la evaluacion no
 * sabe donde debia estar: su dia no se califica y su renglon no aparece
 * en la lista de ningun cliente. Por eso la pantalla lista la PLANTILLA
 * entera y no las modalidades ya dadas de alta —esas no dirian nada de
 * quien falta— y por eso abre con el filtro de "sin asignar" a la mano.
 *
 * La vigencia es lo que permite que un cambio no reescriba la historia:
 * asignar una modalidad nueva CIERRA la anterior en vez de pisarla, y un
 * reporte de hace tres meses se sigue explicando con lo que regia
 * entonces.
 */
@Component({
  selector: "app-modalidades",
  standalone: true,
  templateUrl: "./modalidades.component.html",
  styleUrls: ["./modalidades.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class ModalidadesComponent implements OnInit {
  renglones: ModalidadRenglon[] = [];
  ubicaciones: Ubicacion[] = [];
  loading = false;

  filtroBusqueda = "";
  filtroModalidad: string = null;
  filtroUbicacion: number = null;
  filtroSinAsignar = false;

  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [10, 25, 50, 100];
  totalRegistros = 0;

  constructor(
    private asistenciaService: AsistenciaService,
    private modalDialog: MatDialog,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarUbicaciones();
    queueMicrotask(() => {
      this.consultaPagina(1);
      this.cdr.detectChanges();
    });
  }

  consultaPagina(pagina: number): void {
    this.currentPage = pagina;
    const filtro = this.armarFiltro();
    filtro.rowInicio = (pagina - 1) * this.pageSize;
    filtro.regPagina = this.pageSize;

    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarPlantillaModalidad(filtro)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          this.renglones = respuesta?.list ?? [];
          this.totalRegistros = respuesta?.pTotalItems ?? 0;
          if (respuesta?.pMensaje && respuesta.pCodRetorno !== "00000") {
            this.msg.warningCall(respuesta.pMensaje, null, null);
          }
        },
        error: () => {
          this.renglones = [];
          this.totalRegistros = 0;
          this.msg.errorCall("No se pudo consultar la plantilla.", null, null);
        },
      });
  }

  limpiarFiltros(): void {
    this.filtroBusqueda = "";
    this.filtroModalidad = null;
    this.filtroUbicacion = null;
    this.filtroSinAsignar = false;
    this.consultaPagina(1);
  }

  /**
   * El atajo que resuelve el arranque del modulo: quien no tiene
   * modalidad no se evalua, y encontrarlos uno por uno en una plantilla
   * de cientos no es viable.
   */
  verSoloSinAsignar(): void {
    this.filtroSinAsignar = true;
    this.filtroModalidad = null;
    this.consultaPagina(1);
  }

  asignar(renglon: ModalidadRenglon): void {
    if (this.modalDialog.openDialogs.length > 0) {
      return;
    }
    const dialogo = this.modalDialog.open(ModalModalidadComponent, {
      disableClose: true,
      autoFocus: true,
      width: "820px",
      maxWidth: "calc(100vw - 32px)",
      panelClass: ["custom-modalbox", "modalidad-form-dialog"],
      data: { renglon: { ...renglon }, ubicaciones: this.ubicaciones },
    });
    dialogo.afterClosed().subscribe((guardado) => {
      if (guardado) {
        this.consultaPagina(this.currentPage);
      }
    });
  }

  etiquetaModalidad(renglon: ModalidadRenglon): string {
    const etiquetas: Record<string, string> = {
      OFICINA: "En sitio",
      HOME_OFFICE: "Desde casa",
      MIXTO: "Mixto",
    };
    return etiquetas[renglon.modalidad] ?? "Sin asignar";
  }

  /**
   * La forma tambien dice el estado, no solo el color: quien no distingue
   * rojo de verde tiene que poder leer la tabla igual.
   */
  claseModalidad(renglon: ModalidadRenglon): string {
    if (!renglon.modalidad) {
      return "table-page__status-badge is-danger";
    }
    return "table-page__status-badge is-active";
  }

  /** Donde se le espera. Home office puro no tiene sitio, y esta bien. */
  etiquetaSitio(renglon: ModalidadRenglon): string {
    if (renglon.modalidad === "HOME_OFFICE") {
      return "Su domicilio";
    }
    return renglon.nombreUbicacion || "—";
  }

  etiquetaVigencia(renglon: ModalidadRenglon): string {
    if (!renglon.fechaInicioVigencia) {
      return "—";
    }
    const desde = this.soloFecha(renglon.fechaInicioVigencia);
    if (!renglon.fechaFinVigencia) {
      return `Desde ${desde}`;
    }
    return `${desde} a ${this.soloFecha(renglon.fechaFinVigencia)}`;
  }

  // ------------------------------------------------------------------
  // Paginacion
  // ------------------------------------------------------------------

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRegistros / this.pageSize));
  }

  get showPagination(): boolean {
    return this.totalRegistros > this.pageSize;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  onPageChange(pagina: number): void {
    if (pagina < 1 || pagina > this.totalPages) {
      return;
    }
    this.consultaPagina(pagina);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  onPageSizeChange(tamano: number): void {
    this.pageSize = tamano;
    this.consultaPagina(1);
  }

  getPaginationItems(): Array<number | string> {
    const total = this.totalPages;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const items: Array<number | string> = [1];
    const inicio = Math.max(2, this.currentPage - 1);
    const fin = Math.min(total - 1, this.currentPage + 1);
    if (inicio > 2) {
      items.push("...");
    }
    for (let p = inicio; p <= fin; p++) {
      items.push(p);
    }
    if (fin < total - 1) {
      items.push("...");
    }
    items.push(total);
    return items;
  }

  isPageNumber(item: number | string): boolean {
    return typeof item === "number";
  }

  // ------------------------------------------------------------------

  private armarFiltro(): MonitoreoFiltro {
    const filtro = new MonitoreoFiltro();
    filtro.busqueda = this.filtroBusqueda?.trim() || null;
    filtro.modalidad = this.filtroModalidad;
    filtro.idUbicacion = this.filtroUbicacion;
    filtro.soloSinAsignar = this.filtroSinAsignar ? true : null;
    return filtro;
  }

  private cargarUbicaciones(): void {
    this.asistenciaService.consultarUbicaciones(null).subscribe({
      next: (respuesta) => {
        this.ubicaciones = respuesta?.list ?? [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.ubicaciones = [];
      },
    });
  }

  /** El backend manda yyyy-MM-dd; recortar evita depender de la zona. */
  private soloFecha(valor: string | Date): string {
    const texto = `${valor}`;
    return texto.length >= 10 ? texto.substring(0, 10) : texto;
  }
}
